import { useEffect, useMemo, useRef, useState } from 'react';
import { flightApi } from '../lib/api';
import { ApiSuccess } from '../types/api-response';
import { FlightDetails } from '../interfaces/flight.details';
import { TripType } from '@industronics/fids-utils';

const BASE = process.env.REACT_APP_FIDS_BASE_URL;


export function useFids(airport: string, tripType: TripType) {

    const [trips, setTrips] = useState<FlightDetails[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const esRef = useRef<EventSource | null>(null);

    const streamUrl = useMemo(() => `${process.env.REACT_APP_SOCKET_BASE_URL}/fids/${airport}/stream`, [airport]);

    async function loadSnapshot() {
        if (!airport) return;
        let cancelled = false;
        setLoading(true);
        setErr(null);
        try {
            const res = await flightApi.get<ApiSuccess<FlightDetails[]>>(`${BASE}/fids/trips.json?tripType=${tripType}&airportId=${airport}&isPublicDisplay=true`, {
                headers: { 'Cache-Control': 'no-store' },
            });
            if (!cancelled) setTrips(res.data.data);
        } catch (e: any) {
            if (!cancelled) setErr(e.message ?? 'Failed to load');
        } finally {
            if (!cancelled) setLoading(false);
        }
    }

    useEffect(() => {
        loadSnapshot();
    }, [airport]);

    useEffect(() => {
        let cancelled = false;


        // 2) live stream via SSE
        const es = new EventSource(streamUrl, { withCredentials: false });
        esRef.current = es;
        es.onmessage = (evt) => {
            try {
                const patch = JSON.parse(evt.data);
                const data = patch.data as FlightDetails;
                console.log(`updating:, ${data.flightNumber}, trip: ${data.tripType}`);
                if (data.tripType === tripType) {
                    setTrips((prev) => {
                        const idx = prev.findIndex(x => x._id === data._id);
                        // always work on a copy
                        let next = [...prev];

                        if (idx !== -1) {
                            next[idx] = data;            // replace existing
                        } else {
                            next = [...prev, data];      // append new
                        }

                        // sort (mutates) is fine on our local copy, not on prev
                        next.sort((a, b) => {
                            const ta = new Date(a.scheduledTravelTime).getTime() || 0;
                            const tb = new Date(b.scheduledTravelTime).getTime() || 0;
                            return ta - tb;
                        });

                        return next; // ✅ always return FlightDetails[]
                    });
                }
            } catch {
                // ignore malformed
            }
        };


        es.onerror = () => {
            // Browser will auto-reconnect; optionally show a small “reconnecting” badge.
        };

        return () => {
            cancelled = true;
            es.close();
            esRef.current = null;
        };
    }, [streamUrl]);

    return { trips, loading, error: err };
}
